import React, { useState, useMemo } from "react";
import { getAllProgramsForCollege, seatCategories } from "./data/colleges";
import { getCollegeImagePath, getCollegeGradient } from "./data/universityImages";

/**
 * CollegeDetailModal — shows all programs offered by a specific college.
 * Opened when clicking a college card in the Colleges tab.
 */
const CollegeDetailModal = ({ collegeName, campus, onClose }) => {
    const [activeTab, setActiveTab] = useState("seats");
    const [searchQuery, setSearchQuery] = useState("");

    const allPrograms = useMemo(
        () => getAllProgramsForCollege(collegeName),
        [collegeName]
    );

    const filteredPrograms = useMemo(
        () =>
            allPrograms.filter(
                (p) =>
                    !searchQuery ||
                    p.programName.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        [allPrograms, searchQuery]
    );

    const heroImage = getCollegeImagePath(collegeName);
    const gradient = getCollegeGradient(collegeName);

    // Maps display label → seat key and cutoff key
    const displayCategories = [
        { label: "UR", seatKey: "UR", cutoffKey: "UR" },
        { label: "OBC-NCL", seatKey: "OBC_NCL", cutoffKey: "OBC" },
        { label: "SC", seatKey: "SC", cutoffKey: "SC" },
        { label: "ST", seatKey: "ST", cutoffKey: "ST" },
        { label: "EWS", seatKey: "EWS", cutoffKey: "EWS" },
    ];

    // Group by inferred category from program name
    const categorizeProgram = (name) => {
        if (/B\.Com|B\.A\.\s*\(Hons\.\)\s*Business|Bachelor of Management|BBA|B\.A\.\s*\(Hons\.\)\s*Economics/i.test(name)) return "Commerce & Management";
        if (/B\.Sc|B\.Tech/i.test(name)) return "Science, Math & Tech";
        if (/Music|Fine Arts/i.test(name)) return "Music & Fine Arts";
        if (/B\.Voc/i.test(name)) return "Vocational";
        if (/Elementary Education/i.test(name)) return "Education";
        if (/B\.A\.\s*\(Hons\.\)\s*(Arabic|Bengali|English|French|German|Hindi|Italian|Persian|Punjabi|Russian|Sanskrit|Spanish|Urdu)/i.test(name)) return "Languages";
        return "Arts & Social Sciences";
    };

    const groupedByCategory = useMemo(() => {
        const groups = {};
        filteredPrograms.forEach((p) => {
            const cat = categorizeProgram(p.programName);
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(p);
        });
        return groups;
    }, [filteredPrograms]);

    const getTotalSeats = (seats) => {
        return seatCategories.reduce((sum, cat) => {
            return sum + (typeof seats[cat] === 'number' ? seats[cat] : 0);
        }, 0);
    };

    const totalSeatsAll = filteredPrograms.reduce(
        (sum, p) => sum + getTotalSeats(p.seats),
        0
    );

    const formatCutoff = (val) => {
        if (val === null || val === undefined) return "—";
        return typeof val === 'number' ? val.toFixed(1) : val;
    };

    return (
        <div className="elig-modal-overlay" onClick={onClose}>
            <div
                className="elig-modal elig-college-detail-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Hero Banner */}
                <div
                    className="elig-modal__hero"
                    style={{
                        background: heroImage
                            ? `linear-gradient(to bottom, ${gradient[0]}cc, ${gradient[1]}ee), url(${heroImage}) center/cover`
                            : `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
                    }}
                >
                    <button className="elig-modal__close" onClick={onClose}>
                        ✕
                    </button>
                    <div className="elig-modal__hero-content">
                        <h2 className="elig-modal__title">{collegeName}</h2>
                        <p className="elig-modal__hero-sub">
                            📍 {campus || "Delhi University"} • {allPrograms.length}{" "}
                            {allPrograms.length === 1 ? "Program" : "Programs"} Available
                        </p>
                    </div>
                </div>

                {/* Tabs + Search */}
                <div className="elig-modal__tabs-bar elig-modal__tabs-bar--with-search">
                    <div className="elig-modal__tabs-left">
                        <button
                            className={`elig-modal-tab ${activeTab === "seats" ? "elig-modal-tab--active" : ""}`}
                            onClick={() => setActiveTab("seats")}
                        >
                            📊 Seats
                        </button>
                        <button
                            className={`elig-modal-tab ${activeTab === "cutoffs" ? "elig-modal-tab--active" : ""}`}
                            onClick={() => setActiveTab("cutoffs")}
                        >
                            📈 Cutoffs
                        </button>
                    </div>
                    <div className="elig-modal__search-box">
                        <span>🔍</span>
                        <input
                            type="text"
                            placeholder="Search programs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="elig-modal__body">
                    {/* Stats row */}
                    <div className="elig-college-detail__stats">
                        <div className="elig-college-detail__stat">
                            <span className="elig-college-detail__stat-value">
                                {filteredPrograms.length}
                            </span>
                            <span className="elig-college-detail__stat-label">
                                Programs
                            </span>
                        </div>
                        <div className="elig-college-detail__stat">
                            <span className="elig-college-detail__stat-value">
                                {totalSeatsAll}
                            </span>
                            <span className="elig-college-detail__stat-label">
                                Total Seats
                            </span>
                        </div>
                        <div className="elig-college-detail__stat">
                            <span className="elig-college-detail__stat-value">
                                {Object.keys(groupedByCategory).length}
                            </span>
                            <span className="elig-college-detail__stat-label">
                                Categories
                            </span>
                        </div>
                    </div>

                    {/* Programs table grouped by category */}
                    {Object.entries(groupedByCategory).map(
                        ([category, progs]) => (
                            <div
                                className="elig-college-detail__group"
                                key={category}
                            >
                                <div className="elig-college-detail__group-header">
                                    <h4>{category}</h4>
                                    <span className="elig-college-detail__group-count">
                                        {progs.length}{" "}
                                        {progs.length === 1
                                            ? "program"
                                            : "programs"}
                                    </span>
                                </div>
                                <div className="elig-data-table-wrapper">
                                    <table className="elig-data-table">
                                        <thead>
                                            <tr>
                                                <th>PROGRAM</th>
                                                {displayCategories.map(
                                                    (cat) => (
                                                        <th key={cat.label}>
                                                            {cat.label}
                                                        </th>
                                                    )
                                                )}
                                                {activeTab === "seats" && <th>TOTAL</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {progs.map((prog, i) => (
                                                <tr key={i}>
                                                    <td className="elig-data-table__program-name">
                                                        {prog.programName}
                                                    </td>
                                                    {displayCategories.map(
                                                        (cat) => (
                                                            <td key={cat.label}>
                                                                {activeTab ===
                                                                    "seats"
                                                                    ? (prog.seats[cat.seatKey] || 0)
                                                                    : formatCutoff(prog.cutoffs ? prog.cutoffs[cat.cutoffKey] : null)}
                                                            </td>
                                                        )
                                                    )}
                                                    {activeTab === "seats" && (
                                                        <td className="elig-data-table__total">
                                                            {getTotalSeats(prog.seats)}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )
                    )}

                    {filteredPrograms.length === 0 && (
                        <div className="elig-empty-state">
                            <div className="elig-empty-state__icon">🔍</div>
                            <div className="elig-empty-state__text">
                                No programs match your search.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CollegeDetailModal;
